/**
 * Calendar days in a given timezone. A day is not always 24 hours: on the
 * nights the clocks change it is 23 or 25, so stepping by 86 400 000 ms
 * shows one date twice, or skips one, and moves a weekly lesson an hour.
 * No imports, so the verify scripts can run it.
 */

/** 'YYYY-MM-DD' for a moment, in `tz`. */
export function localDateKey(d: Date, tz: string): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
}

/** A 'YYYY-MM-DD' key moved by n days. */
export function addDaysToKey(key: string, n: number): string {
  const [y, m, d] = key.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
}

/**
 * `count` dates from today's in `tz`, one per calendar day. Each is noon
 * UTC, which falls on that same date anywhere within eleven hours of UTC.
 */
export function localDays(count: number, tz: string, now = new Date()): Date[] {
  const today = localDateKey(now, tz);
  return Array.from({ length: count }, (_, i) => new Date(`${addDaysToKey(today, i)}T12:00:00Z`));
}

/** The wall clock in `tz`, read as if it were UTC, minus the real moment: the offset. */
function offsetMs(at: number, tz: string): number {
  const p = Object.fromEntries(new Intl.DateTimeFormat('en-US', {
    timeZone: tz, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  }).formatToParts(new Date(at)).map((x) => [x.type, Number(x.value)]));
  return Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second) - Math.floor(at / 1000) * 1000;
}

/**
 * The same time on the clock in `tz`, `n` calendar days later: Tuesday
 * 18:00 + 7 is next Tuesday 18:00, whatever the clocks did in between.
 */
export function addLocalDays(d: Date, n: number, tz: string): Date {
  const wall = d.getTime() + offsetMs(d.getTime(), tz) + n * 86_400_000;
  // The offset at the target; asked twice, since the first guess can sit
  // on the other side of a change.
  let t = wall - offsetMs(wall, tz);
  t = wall - offsetMs(t, tz);
  return new Date(t);
}
